// client/src/components/AffiliatesTransactions.js
// Multi-devises complet : affichage, totaux et KPI par devise
// Palette: Ecobill Blue #274C74, Slate #0F172A, Accents #E2E8F0 / #F8FAFC

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../CSS/Dashboard.css';
import AffiliatesNavbar from './AffiliatesNavbar';
import { db, storage } from '../../firebase';
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  setDoc,
  getDoc,
  where,
  documentId,
  getDocFromCache,
  getDocsFromCache,
  limit as fsLimit,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { ref, listAll, getDownloadURL } from 'firebase/storage';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// ============ Cache (sessionStorage) ============
const PROMOS_CACHE_PREFIX = 'afftx_promos_by_uid_v3:'; // + uid  => { ts, data: {promo, transactions}, codesUpdatedAt }
const PROMOS_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

const USERS_CACHE_KEY = 'afftx_users_cache_v1'; // map { [uid]: { pseudo, photoURL, ts } }
const USERS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const ENT_STATUS_CACHE_KEY = 'afftx_ent_status_v1'; // map { [entrepriseUid]: { status: 'active' | 'inactive', ts } }
const ENT_STATUS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min pour refléter rapidement les résiliations

const LOGO_CACHE_PREFIX = 'afftx_logo_by_uid_v1:'; // + entrepriseUid => { ts, url }
const LOGO_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const AFF_LINKED_CACHE_KEY = 'afftx_active_affiliates_v1:'; // + regieUid -> { ts, uids[] }
const AFF_LINKED_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

const getSessionJSON = (key, def = null) => {
  try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
};
const setSessionJSON = (key, val) => {
  try { sessionStorage.setItem(key, JSON.stringify(val)); } catch { /* noop */ }
};
const delSessionKey = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* noop */ }
};

// Promise dedupe (évite requêtes doublons simultanées)
const inflight = new Map();
const withInflight = async (key, fn) => {
  if (inflight.has(key)) return inflight.get(key);
  const promise = (async () => {
    try { return await fn(); } finally { inflight.delete(key); }
  })();
  inflight.set(key, promise);
  return promise;
};

// Détecter hard reload OU ?fresh=1 pour rafraîchir (mais on contrôle la sous-collection avec codesUpdatedAt)
const isHardReload = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('fresh') === '1') return true;
  } catch {}
  try {
    const navs = performance.getEntriesByType && performance.getEntriesByType('navigation');
    if (navs && navs[0]) return navs[0].type === 'reload';
    // Legacy
    // eslint-disable-next-line
    if (performance && performance.navigation) return performance.navigation.type === 1;
  } catch {}
  return false;
};

// ============ Monnaies (autorise et normalise) ============
const ALLOWED_CURRENCIES = ['EUR', 'USD', 'CAD', 'GBP', 'CHF', 'XOF'];
const normalizeCurrency = (cur) => {
  if (!cur) return 'EUR';
  const s = String(cur).trim().toUpperCase();
  if (s === 'CFA' || s === 'FCFA') return 'XOF';
  return ALLOWED_CURRENCIES.includes(s) ? s : 'EUR';
};

// ============ Commissions récurrentes par devise (ajuste si besoin) ============
const COMMISSION_BY_CURRENCY = {
  EUR: 15.75,
  USD: 17.0,
  CAD: 22.0,
  GBP: 14.0,
  CHF: 16.0,
  XOF: 1750,
};

// Recurring commissions run from the 3rd up to the 12th month (diff offset <= 11)
const MAX_RECURRING_MONTHS = 11;
const isEligibleRecurringMonth = (diff) =>
  Number.isFinite(diff) && diff >= 2 && diff <= MAX_RECURRING_MONTHS;

// Formatage montants — XOF sans décimales, sinon 2 décimales (symbole local fr-FR)
const formatAmount = (amount, currency) => {
  const cur = normalizeCurrency(currency);
  const n = Number(amount || 0);
  const fmt = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: cur,
    minimumFractionDigits: cur === 'XOF' ? 0 : 2,
    maximumFractionDigits: cur === 'XOF' ? 0 : 2,
  });
  return fmt.format(cur === 'XOF' ? Math.round(n) : n);
};

const roundByCurrency = (value, currency) => {
  const cur = normalizeCurrency(currency);
  if (!Number.isFinite(value)) return 0;
  if (cur === 'XOF') return Math.round(value);
  return Math.round(value * 100) / 100;
};

// Liste propre d'un objet {EUR: 10, USD:20} → tableau trié par devise autorisée
const objectToCurrencyList = (map) => {
  const keys = Object.keys(map || {});
  const ordered = [...ALLOWED_CURRENCIES.filter(c => keys.includes(c)), ...keys.filter(k => !ALLOWED_CURRENCIES.includes(k))];
  return ordered.map(k => ({ currency: k, amount: Number(map[k] || 0) }));
};

const AffiliatesTransactions = () => {
  const [promo, setPromo] = useState(null);
  const [transactions, setTransactions] = useState([]);           // 👉 toutes les transactions affichées (self + affiliés si régie)
  const [transactionsSelf, setTransactionsSelf] = useState([]);    // 👉 directes uniquement
  const [transactionsAff, setTransactionsAff] = useState([]);      // 👉 indirectes (affiliés actifs)
  const [loading, setLoading] = useState(true);
  const [userPseudos, setUserPseudos] = useState({});
  const [companyLogos, setCompanyLogos] = useState({});

  // eslint-disable-next-line
  const [sumByCurrencyCurrent, setSumByCurrencyCurrent] = useState({});
  // eslint-disable-next-line
  const [sumByCurrencyNext, setSumByCurrencyNext] = useState({});
  // eslint-disable-next-line
  const [sumByCurrencyTotal, setSumByCurrencyTotal] = useState({});

  // ➕ Indirect sums (affiliés) — conservés pour compatibilité mais non affichés
  // eslint-disable-next-line
  const [indSumCurrent, setIndSumCurrent] = useState({});
  // eslint-disable-next-line
  const [indSumNext, setIndSumNext] = useState({});
  // eslint-disable-next-line
  const [indSumTotal, setIndSumTotal] = useState({});

  // ✅ NOUVEAU : KPI “vue” (les 3 carrés, depend du sélecteur)
  const [viewSumCurrent, setViewSumCurrent] = useState({});
  const [viewSumNext, setViewSumNext] = useState({});
  const [viewClientsCount, setViewClientsCount] = useState(0);

  // eslint-disable-next-line
  const fileInputRef = useRef(null);

  const [activeSection, setActiveSection] = useState("current");
  const [currentUser, setCurrentUser] = useState(null);

  const [role, setRole] = useState(null);
  const [affiliateRegieUid, setAffiliateRegieUid] = useState(null);
  // eslint-disable-next-line
  const [regieDisplayName, setRegieDisplayName] = useState('');
  const roleLc = String(role || '').trim().toLowerCase();
  const isRegie = role === 'AffiliateRegie';
  const isAffiliateUser = ['affiliates', 'affiliatepending'].includes(roleLc);
  const shouldHideAmounts = isAffiliateUser && !!affiliateRegieUid;
  const showKpiCards = !shouldHideAmounts;
  const showAmountColumn = !shouldHideAmounts;
  // eslint-disable-next-line no-unused-vars
  const sectionMarginTop = showKpiCards ? '50px' : '20px';

  // ===== Filtre propriétaire (vous/affiliés) — déplacé visuellement sous le titre
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all' | 'self' | 'all_aff' | <uid>

  const extractEntrepriseUid = (timestamp) => (timestamp?.uid || '');
  const extractDateTime = (timestamp) => (timestamp?.datetime || '');

  const parseUsedTimestamp = (timestampStr) => {
    if (!timestampStr) return new Date();
    if (timestampStr.includes("/")) {
      const parts = timestampStr.split(" ");
      const datePart = parts[0];
      const timePart = parts[1] || '00:00:00';
      const [dayStr, monthStr, yearStr] = datePart.split("/");
      const [hourStr, minuteStr, secondStr] = timePart.split(":");
      return new Date(
        parseInt(yearStr, 10),
        parseInt(monthStr, 10) - 1,
        parseInt(dayStr, 10),
        parseInt(hourStr || '0', 10),
        parseInt(minuteStr || '0', 10),
        parseInt(secondStr || '0', 10)
      );
    }
    return new Date(timestampStr);
  };

  const formatRawDateForDisplay = (value) => {
    if (!value) return '';
    const str = String(value).trim();
    if (!str) return '';
    const slashMatch = str.match(/^(\d{2}\/\d{2}\/\d{4})/);
    if (slashMatch) return slashMatch[1];
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      return `${d}/${m}/${y}`;
    }
    const dateObj = parseUsedTimestamp(str);
    if (dateObj instanceof Date && Number.isFinite(dateObj.getTime())) {
      return dateObj.toLocaleDateString('fr-FR');
    }
    // eslint-disable-next-line
    const genericMatch = str.match(/^(\d{2})[\-\.](\d{2})[\-\.](\d{4})/);
    if (genericMatch) {
      const [, d, m, y] = genericMatch;
      return `${d}/${m}/${y}`;
    }
    return str;
  };

  // ===== Entreprise active cache (mémoire + session) =====
  const companyStatusCache = useRef({});
  useEffect(() => {
    const persisted = getSessionJSON(ENT_STATUS_CACHE_KEY, {});
    if (persisted) companyStatusCache.current = persisted;
  }, []);

  const readEntStatusCache = (uid) => {
    const item = companyStatusCache.current[uid];
    if (!item) return null;
    if (Date.now() - (item.ts || 0) > ENT_STATUS_CACHE_TTL_MS) return null;
    if (typeof item.status === 'string') return item.status;
    if (typeof item.active === 'boolean') return item.active ? 'active' : 'inactive';
    return null;
  };
  const writeEntStatusCache = (uid, status) => {
    companyStatusCache.current[uid] = {
      status: status === 'active' ? 'active' : 'inactive',
      ts: Date.now()
    };
    setSessionJSON(ENT_STATUS_CACHE_KEY, companyStatusCache.current);
  };

  const checkCompanyStatus = useCallback(async (entrepriseUid, { forceFresh = false } = {}) => {
    if (!entrepriseUid) return 'inactive';

    if (!forceFresh) {
      const cached = readEntStatusCache(entrepriseUid);
      if (cached !== null) return cached;
    }

    try {
      const qy = query(
        collection(db, "entreprises"),
        where("userId", "==", entrepriseUid),
        fsLimit(10) // Firestore rule: list queries must include an explicit limit (<=50)
      );
      const querySnapshot = await getDocs(qy);

      let status = 'inactive';
      querySnapshot.forEach((docSnap) => {
        if ((docSnap.data()?.base || '') === 'active') status = 'active';
      });

      writeEntStatusCache(entrepriseUid, status);
      return status;
    } catch (error) {
      console.error("Erreur statut entreprise:", entrepriseUid, error);
      writeEntStatusCache(entrepriseUid, 'inactive');
      return 'inactive';
    }
    // eslint-disable-next-line
  }, [db]);

  // ===== Cache promos/transactions =====
  const readPromosCache = (uid) => {
    const key = PROMOS_CACHE_PREFIX + uid;
    const cached = getSessionJSON(key);
    if (!cached) return null;
    if (Date.now() - (cached.ts || 0) > PROMOS_CACHE_TTL_MS) return null;
    return cached.data || null;
  };
  const writePromosCache = (uid, data) => {
    const key = PROMOS_CACHE_PREFIX + uid;
    setSessionJSON(key, { ts: Date.now(), data });
  };
  const invalidatePromosCache = (uid) => {
    delSessionKey(PROMOS_CACHE_PREFIX + uid);
  };

  // ===== Cache users (pseudo, photoURL) =====
  const readUsersCacheMap = () => {
    const map = getSessionJSON(USERS_CACHE_KEY, {}) || {};
    // Purge TTL
    const now = Date.now();
    const purged = {};
    for (const k of Object.keys(map)) {
      if (now - (map[k].ts || 0) <= USERS_CACHE_TTL_MS) purged[k] = map[k];
    }
    if (Object.keys(purged).length !== Object.keys(map).length) {
      setSessionJSON(USERS_CACHE_KEY, purged);
    }
    return purged;
  };
  const writeUsersCacheMap = (mut) => {
    setSessionJSON(USERS_CACHE_KEY, mut);
  };

  // ===== Cache logos =====
  const readLogoCache = (entUid) => {
    const key = LOGO_CACHE_PREFIX + entUid;
    const cached = getSessionJSON(key);
    if (!cached) return null;
    if (Date.now() - (cached.ts || 0) > LOGO_CACHE_TTL_MS) return null;
    return cached.url || null;
  };
  const writeLogoCache = (entUid, url) => {
    const key = LOGO_CACHE_PREFIX + entUid;
    setSessionJSON(key, { ts: Date.now(), url: url || null });
  };

  // ===== Active affiliates for a regie (cache + filters) =====
  const readAffLinkedCache = (regieUid) => {
    const key = AFF_LINKED_CACHE_KEY + regieUid;
    const cached = getSessionJSON(key);
    if (!cached) return null;
    if (Date.now() - (cached.ts || 0) > AFF_LINKED_CACHE_TTL_MS) return null;
    return cached.uids || null;
  };
  const writeAffLinkedCache = (regieUid, uids) => {
    const key = AFF_LINKED_CACHE_KEY + regieUid;
    setSessionJSON(key, { ts: Date.now(), uids: Array.isArray(uids) ? uids : [] });
  };

  const getActiveAffiliateUidsForRegie = useCallback(async (regieUid, { forceFresh = false } = {}) => {
    if (!regieUid) return [];
    if (!forceFresh) {
      const cached = readAffLinkedCache(regieUid);
      if (cached) return cached;
    }

    return withInflight(`afftx_affuids:${regieUid}:${forceFresh ? 'fresh' : 'cached'}`, async () => {
      const found = new Set();

      // 1) Sous-collec regies/{regieUid}/affiliates
      try {
        const affCol = collection(db, 'regies', regieUid, 'affiliates');
        let snap = null;
        if (!forceFresh) {
          try { snap = await getDocsFromCache(affCol); } catch { snap = null; }
        }
        if (!snap) snap = await getDocs(affCol);

        snap.forEach(d => {
          const id = String(d.id || '').trim();
          const explicit = String((d.data() || {}).affiliateUid || '').trim();
          if (id) found.add(id);
          if (explicit) found.add(explicit);
        });
      } catch {}

      // 2) Fallback users where regieUid == current
      try {
        const qy = query(collection(db, 'users'), where('regieUid', '==', regieUid));
        let snap = null;
        if (!forceFresh) {
          try { snap = await getDocsFromCache(qy); } catch { snap = null; }
        }
        if (!snap) snap = await getDocs(qy);

        snap.forEach(d => {
          const data = d.data() || {};
          const role = String(data.role || '').trim().toLowerCase();
          if (['affiliates', 'affiliatepending'].includes(role)) {
            found.add(String(d.id));
          }
        });
      } catch {}

      // 3) Filtrer affiliés éligibles (affiliates + affiliatepending, hors statuts bannis/suspendus)
      const arr = Array.from(found);
      const eligible = new Set();
      const allowedRoles = new Set(['affiliates', 'affiliatepending']);
      const blockedStatuses = new Set(['banned', 'suspended', 'disabled', 'archived', 'resiliated']);
      const CHUNK = 10;
      for (let i = 0; i < arr.length; i += CHUNK) {
        const slice = arr.slice(i, i + CHUNK);
        try {
          const qUsers = query(collection(db, 'users'), where(documentId(), 'in', slice));
          let snap = null;
          if (!forceFresh) {
            try { snap = await getDocsFromCache(qUsers); } catch { snap = null; }
          }
          if (!snap) snap = await getDocs(qUsers);

          snap.forEach(d => {
            const data = d.data() || {};
            const status = String(data.Status || '').trim().toLowerCase();
            const role = String(data.role || '').trim().toLowerCase();
            if (allowedRoles.has(role) && !blockedStatuses.has(status)) eligible.add(d.id);
          });
        } catch {
          for (const u of slice) {
            try {
              const ref = doc(db, 'users', u);
              let ds = null;
              if (!forceFresh) {
                try { ds = await getDocFromCache(ref); } catch { ds = null; }
              }
              if (!ds) ds = await getDoc(ref);
              if (ds.exists()) {
                const data = ds.data() || {};
                const status = String(data.Status || '').trim().toLowerCase();
                const role = String(data.role || '').trim().toLowerCase();
                if (allowedRoles.has(role) && !blockedStatuses.has(status)) eligible.add(u);
              }
            } catch {}
          }
        }
      }

      const eligibleArr = arr.filter(u => eligible.has(u));
      writeAffLinkedCache(regieUid, eligibleArr);
      return eligibleArr;
    });
    // eslint-disable-next-line
  }, [db]);

  // ===== Fetch promos + transactions (avec contrôle de version) — pour un UID donné (self OU affilié) =====
  const fetchPromoCodesForUid = useCallback(async (uid, { forceFresh = false } = {}) => {
    if (!uid) return { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 };
    try {
      // cache
      let cached = null;
      if (!forceFresh) {
        cached = readPromosCache(uid);
        if (cached) {
          // Validation version parent
          try {
            const parentSnap = await getDoc(doc(db, 'code promo', uid));
            const serverUpdatedAt = parentSnap.exists() ? (parentSnap.data()?.codesUpdatedAt?.seconds || 0) : 0;
            const cacheUpdatedAt  = cached.codesUpdatedAt || 0;
            if (serverUpdatedAt <= cacheUpdatedAt) {
              const withOwner = (cached.transactions || []).map(t => ({ ...t, __ownerUid: uid }));
              return { promo: cached.promo || null, transactions: withOwner, hasPromo: cached.hasPromo, codesUpdatedAt: cacheUpdatedAt };
            }
          } catch {
            const withOwner = (cached.transactions || []).map(t => ({ ...t, __ownerUid: uid }));
            return { promo: cached.promo || null, transactions: withOwner, hasPromo: cached.hasPromo, codesUpdatedAt: cached.codesUpdatedAt || 0 };
          }
        }
      }

      // parent
      const userDocRef = doc(db, 'code promo', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        writePromosCache(uid, { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 });
        return { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 };
      }

      const parentData = userDocSnap.data() || {};
      const parentCodesUpdatedAt = parentData?.codesUpdatedAt?.seconds || 0;

      // codes
      const codesCollectionRef = collection(userDocRef, 'codes');
      const qy = query(codesCollectionRef, orderBy("createdAt", "asc"));
      const codesSnapshot = await getDocs(qy);

      const codesArray = [];
      for (const docSnap of codesSnapshot.docs) {
        const data = docSnap.data();
        // Charger la sous-collection activations (nouvelle structure)
        const activSnap = await getDocs(collection(docSnap.ref, 'activations'));
        const newActivations = activSnap.docs.map(a => ({ id: a.id, ...a.data() }));
        // Fusionner avec l'ancien tableau usedTimestamps (rétro-compat)
        const oldTs = Array.isArray(data.usedTimestamps) ? data.usedTimestamps
          : (data.usedTimestamps ? [data.usedTimestamps] : []);
        const mergedTs = [...newActivations, ...oldTs];
        codesArray.push({
          id: docSnap.id,
          ...data,
          usedTimestamps: mergedTs.length > 0 ? mergedTs : undefined,
        });
      }

      const firstPromo = codesArray.length > 0 ? codesArray[0] : parentData;
      const transactionsWithTimestamps = codesArray.filter(code => code.usedTimestamps);

      // Statut entreprise (actif / résilié) + enrichissement transactions
      const filteredTransactions = [];
      for (const trans of transactionsWithTimestamps) {
        const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
        const annotated = (await Promise.all(
          timestamps.map(async (rawTs) => {
            if (!rawTs) return null;
            const normalizedTs = typeof rawTs === 'object' ? { ...rawTs } : { uid: String(rawTs || '') };
            const entrepriseUid = normalizedTs?.uid || String(rawTs || '');
            if (!entrepriseUid) {
              normalizedTs.__companyStatus = 'active';
              return normalizedTs;
            }
            const status = await checkCompanyStatus(entrepriseUid, { forceFresh });
            normalizedTs.__companyStatus = status || 'inactive';
            return normalizedTs;
          })
        )).filter(Boolean);

        if (!annotated.length) continue;
        const hasActiveCompany = annotated.some(ts => (ts.__companyStatus || 'inactive') === 'active');
        filteredTransactions.push({
          ...trans,
          usedTimestamps: annotated,
          __ownerUid: uid,
          __hasActiveCompany: hasActiveCompany,
          __companyStatus: hasActiveCompany ? 'active' : 'inactive',
        });
      }

      const hasPromo = !!(firstPromo && (firstPromo.promoCode ? String(firstPromo.promoCode).trim() !== '' : true));
      writePromosCache(uid, {
        promo: firstPromo || null,
        transactions: filteredTransactions, // stock sans __ownerUid (ajouté à la lecture du cache)
        hasPromo,
        codesUpdatedAt: parentCodesUpdatedAt || Math.floor(Date.now() / 1000),
      });

      return { promo: firstPromo || null, transactions: filteredTransactions, hasPromo, codesUpdatedAt: parentCodesUpdatedAt || 0 };
    } catch (error) {
      console.error("Erreur get codes promo pour uid", uid, error);
      return { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 };
    }
  }, [checkCompanyStatus]);

  // ===== Auth + rôle =====
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);
      if (!user) {
        setRole(null);
        setAffiliateRegieUid(null);
        setRegieDisplayName('');
        setLoading(false);
        return;
      }
      try {
        const uref = doc(db, 'users', user.uid);
        const usnap = await getDoc(uref);
        if (usnap.exists()) {
          const data = usnap.data() || {};
          const r = String(data.role || '').trim();
          const regieUid = data.regieUid ? String(data.regieUid).trim() : null;
          setRole(r || null);
          setAffiliateRegieUid(regieUid || null);
        } else {
          setRole(null);
          setAffiliateRegieUid(null);
        }
      } catch {
        setRole(null);
        setAffiliateRegieUid(null);
      }
    });
    return () => unsub();
    // eslint-disable-next-line
  }, [db]);

  useEffect(() => {
    let cancelled = false;
    const fetchRegieLabel = async () => {
      if (!affiliateRegieUid) {
        if (!cancelled) setRegieDisplayName('');
        return;
      }
      try {
        const regieRef = doc(db, 'users', affiliateRegieUid);
        const snap = await getDoc(regieRef);
        if (cancelled) return;
        if (!snap.exists()) {
          setRegieDisplayName('');
          return;
        }
        const data = snap.data() || {};
        const labelCandidates = [
          data.pseudo,
          data.displayName,
          data.entreprise_name,
          data.companyName,
        ]
          .map((v) => (typeof v === 'string' ? v.trim() : ''))
          .filter(Boolean);
        setRegieDisplayName(labelCandidates[0] || '');
      } catch {
        if (!cancelled) setRegieDisplayName('');
      }
    };
    fetchRegieLabel();
    return () => {
      cancelled = true;
    };
  }, [affiliateRegieUid]);

  const uid = currentUser ? currentUser.uid : null;

  // ===== Helpers monnaies =====
  const getCurrencyForTrans = (trans, ts) => {
    if (ts?.currency) return normalizeCurrency(ts.currency);
    if (trans?.currency) return normalizeCurrency(trans.currency);
    return 'EUR';
  };

  // ===== Filtrage/section (multi-devises) — paramétrable par liste =====
  const getFilteredTransactionsFromList = useCallback((sourceList, section) => {
    const list = Array.isArray(sourceList) ? sourceList : [];
    if (!list.length) return [];

    // min date per entreprise uid (pour displayDate stable)
    const uidMinDateIso = new Map();
    list.forEach(trans => {
      const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
      timestamps.forEach(ts => {
        const uidE = ts?.uid || String(ts);
        const dateIso = ts?.datetime || extractDateTime(ts);
        const prev = uidMinDateIso.get(uidE);
        if (!prev || new Date(dateIso) < new Date(prev)) uidMinDateIso.set(uidE, dateIso);
      });
    });

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear  = now.getFullYear();
    const previousMonth     = currentMonth === 0 ? 11 : currentMonth - 1;
    const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const nextMonth = (currentMonth + 1) % 12;
    const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    const result = [];
    let sequence = 0;

    const pushEntry = (entry) => {
      const seq = sequence++;
      const baseKey = entry.__rowKey || `ROW-${seq}`;
      result.push({
        ...entry,
        __seq: seq,
        __rowKey: `${baseKey}#${seq}`, // suffix sequence to guarantee uniqueness in React keys
      });
    };

    const buildAnnualProjectionEntry = (trans, ts, anchorYear, anchorMonth) => {
      const ownerUid = trans.__ownerUid || '';
      const uidE = ts?.uid || String(ts);
      const anchorDate = new Date(anchorYear, anchorMonth, 1);
      const anchorIso = anchorDate.toISOString();
      const originalDate =
        ts?.datetime ||
        ts?.displayDate ||
        extractDateTime(ts) ||
        uidMinDateIso.get(uidE) ||
        anchorIso;
      const originalDateObj = parseUsedTimestamp(originalDate);
      const originalDateMs = originalDateObj instanceof Date && Number.isFinite(originalDateObj.getTime())
        ? originalDateObj.getTime()
        : anchorDate.getTime();
      const currency = getCurrencyForTrans(trans, ts);
      const companyStatus = (ts?.__companyStatus || trans?.__companyStatus || 'active').toLowerCase();
      const isResiliated = companyStatus === 'inactive';
      const recordedRaw = Number(ts?.recordedAmount ?? ts?.amount ?? 0);
      const grossRaw = Number(ts?.grossAmount ?? 0);
      const fallbackBase = Number.isFinite(recordedRaw) && recordedRaw > 0
        ? recordedRaw
        : (Number.isFinite(grossRaw) && grossRaw > 0 ? grossRaw : 0);
      const month1Stored = Number(ts?.month1Amount ?? 0);
      const month2Stored = Number(ts?.month2Amount ?? 0);
      const recurringStored = Number(ts?.recurringAmount ?? 0);
      const eff1 = month1Stored > 0 ? month1Stored : fallbackBase;
      const eff2 = month2Stored > 0 ? month2Stored : eff1;
      const recurringMonthlyAmount = recurringStored > 0
        ? recurringStored
        : (grossRaw > 0
          ? roundByCurrency(grossRaw * 0.35, currency)
          : (COMMISSION_BY_CURRENCY[currency] ?? COMMISSION_BY_CURRENCY.EUR));
      const recurringMonthsCount = 9;
      const firstTwoMonthsTotalRaw = eff1 + eff2;
      const recurringTotalRaw = recurringMonthlyAmount * recurringMonthsCount;
      const projectedRaw = firstTwoMonthsTotalRaw + recurringTotalRaw;
      const firstTwoMonthsTotal = roundByCurrency(firstTwoMonthsTotalRaw, currency);
      const recurringTotal = roundByCurrency(recurringTotalRaw, currency);
      const projected = roundByCurrency(projectedRaw, currency);
      const safeAmount = isResiliated ? 0 : projected;
      return {
        ...trans,
        usedTimestamps: {
          ...ts,
          uid: uidE,
          datetime: originalDate,
          displayDate: originalDate,
          __companyStatus: companyStatus,
        },
        amount: safeAmount,
        currency,
        __ownerUid: ownerUid,
        __sortTs: originalDateMs,
        __isCommission: false,
        __isAnnualProjection: true,
        __annualDetails: isResiliated ? null : {
          month1Amount: roundByCurrency(eff1, currency),
          month2Amount: roundByCurrency(eff2, currency),
          firstTwoMonthsTotal,
          recurringMonthlyAmount: roundByCurrency(recurringMonthlyAmount, currency),
          recurringMonthsCount,
          recurringTotal,
          originalStartDate: originalDate,
        },
        __companyStatus: companyStatus,
        __isResiliated: isResiliated,
        __rowKey: `AN-${ownerUid}-${trans.id || 'tx'}-${uidE}-${anchorIso}-${currency}`,
        __annualAnchorIso: anchorIso,
      };
    };

    const addEntry = (trans, isCommission = false, monthYear, forcedCurrency, monthDiff = 0) => {
      const ownerUid = trans.__ownerUid || '';
      if (isCommission) {
        const rawTs = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps[0] : trans.usedTimestamps;
        const uidE = monthYear?.uid || rawTs?.uid || String(rawTs);
        const originalDate =
          rawTs?.datetime ||
          rawTs?.displayDate ||
          extractDateTime(rawTs) ||
          uidMinDateIso.get(uidE) ||
          monthYear?.datetime;
        const currency = forcedCurrency || 'EUR';
        const companyStatus = (monthYear?.status || trans?.__companyStatus || 'active').toLowerCase();
        const isResiliated = companyStatus === 'inactive';
        const sortDate = (() => {
          const base = originalDate || monthYear?.datetime;
          const parsed = base ? parseUsedTimestamp(base) : null;
          const tsMs = parsed instanceof Date ? parsed.getTime() : NaN;
          return Number.isFinite(tsMs) ? tsMs : 0;
        })();
        const recurringStoredCom = Number(rawTs?.recurringAmount ?? 0);
        const grossRawCom = Number(rawTs?.grossAmount ?? 0);
        const amountValue = isResiliated
          ? 0
          : (recurringStoredCom > 0
            ? recurringStoredCom
            : (grossRawCom > 0
              ? roundByCurrency(grossRawCom * 0.35, currency)
              : (COMMISSION_BY_CURRENCY[currency] ?? COMMISSION_BY_CURRENCY.EUR)));
        const entry = {
          ...trans,
          usedTimestamps: {
            ...(rawTs || {}),
            uid: uidE,
            datetime: originalDate,
            displayDate: originalDate,
            __companyStatus: companyStatus,
          },
          amount: amountValue,
          __isCommission: true,
          currency,
          __ownerUid: ownerUid,
          __sortTs: sortDate,
          __companyStatus: companyStatus,
          __isResiliated: isResiliated,
        };
        pushEntry({
          ...entry,
          __rowKey: `COM-${ownerUid}-${trans.id || 'tx'}-${uidE}-${monthYear.datetime}-${currency}`,
        });
      } else {
        const ts = trans.usedTimestamps || {};
        const uidE = ts?.uid || String(ts);
        const originalDate = ts?.datetime || ts?.displayDate || extractDateTime(ts) || uidMinDateIso.get(uidE);
        const recorded = Number(ts.recordedAmount ?? ts.amount ?? 0);
        const gross    = Number(ts.grossAmount ?? 0);
        const companyStatus = (ts?.__companyStatus || trans?.__companyStatus || 'active').toLowerCase();
        const isResiliated = companyStatus === 'inactive';
        const fallbackAmt = recorded > 0 ? recorded : gross;
        const eff1 = Number(ts.month1Amount ?? 0) > 0 ? Number(ts.month1Amount) : fallbackAmt;
        const eff2 = Number(ts.month2Amount ?? 0) > 0 ? Number(ts.month2Amount) : fallbackAmt;
        const rawAmount = monthDiff === 0 ? eff1 : eff2;
        const amount    = isResiliated ? 0 : rawAmount;
        const currency = forcedCurrency || getCurrencyForTrans(trans, ts);
        const sortDate = (() => {
          const base = ts?.datetime || ts?.displayDate || extractDateTime(ts);
          const parsed = base ? parseUsedTimestamp(base) : null;
          const tsMs = parsed instanceof Date ? parsed.getTime() : NaN;
          return Number.isFinite(tsMs) ? tsMs : 0;
        })();
        const entry = {
          ...trans,
          usedTimestamps: {
            ...ts,
            uid: uidE,
            datetime: originalDate,
            displayDate: originalDate,
            __companyStatus: companyStatus,
          },
          amount,
          __baseAmount: eff1,
          __month2Amount: eff2,
          __isCommission: false,
          currency,
          __ownerUid: ownerUid,
          __sortTs: sortDate,
          __companyStatus: companyStatus,
          __isResiliated: isResiliated,
        };
        pushEntry({ ...entry, __rowKey: `TX-${ownerUid}-${trans.id}-${uidE}-${originalDate}-${currency}` });
      }
    };

    list.forEach(trans => {
      const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
      timestamps.forEach(ts => {
        if (!ts || !ts.datetime) return;
        const transWithTs = { ...trans, usedTimestamps: ts };
        const dateIso = ts.datetime || extractDateTime(ts);
        const transDate = parseUsedTimestamp(dateIso);
        const diffToCurrent  = (currentYear - transDate.getFullYear()) * 12 + (currentMonth  - transDate.getMonth());
        const diffToPrevious = (previousMonthYear - transDate.getFullYear()) * 12 + (previousMonth - transDate.getMonth());
        const diffToNext     = (nextMonthYear - transDate.getFullYear()) * 12 + (nextMonth - transDate.getMonth());

        const status = (ts?.__companyStatus || trans?.__companyStatus || 'active').toLowerCase();
        const commFor = (year, month) => ({
          uid: ts?.uid || String(ts),
          datetime: new Date(year, month, 1).toISOString(),
          status,
        });

        const currency = getCurrencyForTrans(trans, ts);
        const billingIntervalRaw = String(ts?.billingInterval || trans?.billingInterval || '').trim().toLowerCase();
        const isAnnual = billingIntervalRaw.startsWith('ann');

        if (section === 'current') {
          if (isAnnual) {
            if (transDate.getFullYear() === currentYear && transDate.getMonth() === currentMonth) {
              pushEntry(buildAnnualProjectionEntry(trans, ts, transDate.getFullYear(), transDate.getMonth()));
            }
            return;
          }
          if (transDate.getFullYear() === currentYear && transDate.getMonth() === currentMonth) {
            addEntry(transWithTs, false, null, currency, diffToCurrent); // mois 1 (diff=0)
          }
          if (transDate.getFullYear() === previousMonthYear && transDate.getMonth() === previousMonth) {
            addEntry(transWithTs, false, null, currency, diffToCurrent); // mois 2 (diff=1)
          }
          if (isEligibleRecurringMonth(diffToPrevious)) {
            addEntry(transWithTs, true, commFor(previousMonthYear, previousMonth), currency);
          }
          if (isEligibleRecurringMonth(diffToCurrent)) {
            addEntry(transWithTs, true, commFor(currentYear, currentMonth), currency);
          }

        } else if (section === 'next') {
          if (isAnnual) return;
          if (transDate.getFullYear() === currentYear && transDate.getMonth() === currentMonth) {
            addEntry(transWithTs, false, null, currency, diffToNext); // mois 2 le mois prochain (diff=1)
          }
          if (isEligibleRecurringMonth(diffToNext)) {
            addEntry(transWithTs, true, commFor(nextMonthYear, nextMonth), currency);
          }

        } else { // total
          if (isAnnual) {
            pushEntry(buildAnnualProjectionEntry(trans, ts, transDate.getFullYear(), transDate.getMonth()));
            return;
          }
          addEntry(transWithTs, false, null, currency);

          const lastEntry = result[result.length - 1];
          if (lastEntry && !lastEntry.__isResiliated) {
            const totalEff1 = Number(lastEntry.__baseAmount ?? lastEntry.amount ?? 0);
            const totalEff2 = Number(lastEntry.__month2Amount ?? lastEntry.__baseAmount ?? lastEntry.amount ?? 0);
            const recurringStoredTotal = Number(ts?.recurringAmount ?? 0);
            const grossForTotal = Number(ts?.grossAmount ?? 0);
            const recurringMonthly = recurringStoredTotal > 0
              ? recurringStoredTotal
              : (grossForTotal > 0
                ? roundByCurrency(grossForTotal * 0.35, currency)
                : (COMMISSION_BY_CURRENCY[currency] ?? COMMISSION_BY_CURRENCY.EUR));
            const rawDiff = Number.isFinite(diffToCurrent) ? diffToCurrent : 0;
            const clampedDiff = Math.min(Math.max(rawDiff, 0), MAX_RECURRING_MONTHS);
            const monthsElapsed = rawDiff < 0 ? 0 : Math.min(clampedDiff + 1, 12);
            const baseMonthsApplied = Math.min(monthsElapsed, 2);
            const recurringMonthsApplied = Math.max(0, Math.min(monthsElapsed - 2, 10));
            const basePortion = roundByCurrency(
              (baseMonthsApplied >= 1 ? totalEff1 : 0) + (baseMonthsApplied >= 2 ? totalEff2 : 0),
              currency
            );
            const recurringTotal = roundByCurrency(recurringMonthly * recurringMonthsApplied, currency);
            const totalAmount = roundByCurrency(basePortion + recurringTotal, currency);

            lastEntry.amount = totalAmount;
            if (baseMonthsApplied > 0 || recurringMonthsApplied > 0) {
              lastEntry.__totalDetails = {
                month1Amount: roundByCurrency(totalEff1, currency),
                month2Amount: roundByCurrency(totalEff2, currency),
                basePortion,
                recurringMonthlyAmount: roundByCurrency(recurringMonthly, currency),
                recurringMonthsApplied,
                recurringTotal,
              };
            } else {
              lastEntry.__totalDetails = null;
            }
          }
        }
      });
    });

    const getSortTimestamp = (entry) => {
      const direct = Number(entry?.__sortTs);
      if (Number.isFinite(direct)) return direct;
      const raw =
        entry?.usedTimestamps?.datetime ||
        entry?.usedTimestamps?.displayDate ||
        extractDateTime(entry?.usedTimestamps);
      if (raw) {
        const parsed = parseUsedTimestamp(raw);
        if (parsed instanceof Date && Number.isFinite(parsed.getTime())) {
          return parsed.getTime();
        }
      }
      return 0;
    };

    result.sort((a, b) => {
      const tb = getSortTimestamp(b);
      const ta = getSortTimestamp(a);
      if (tb !== ta) return tb - ta;
      const sa = Number.isFinite(a.__seq) ? a.__seq : 0;
      const sb = Number.isFinite(b.__seq) ? b.__seq : 0;
      return sb - sa;
    });

    return result;
  }, []);

  const sumListByCurrency = (list) => {
    const map = {};
    list.forEach(t => {
      const cur = normalizeCurrency(t.currency);
      map[cur] = (map[cur] || 0) + Number(t.amount || 0);
    });
    return map;
  };

  // ===== Chargement des données selon rôle =====
  useEffect(() => {
    const run = async () => {
      if (!uid) { setLoading(false); return; }

      setLoading(true);

      // 1) self
      const forceFresh = isHardReload();
      const selfData = await fetchPromoCodesForUid(uid, { forceFresh });
      setPromo(selfData.promo || null);
      setTransactionsSelf(selfData.transactions || []);

      if (!isRegie) {
        setTransactions(selfData.transactions || []);
        setTransactionsAff([]);
        setLoading(false);
        return;
      }

      // 2) affiliés actifs
      const affUids = await getActiveAffiliateUidsForRegie(uid, { forceFresh });
      let affAll = [];
      if (affUids.length) {
        const settled = await Promise.allSettled(
          affUids.map(aUid => fetchPromoCodesForUid(aUid, { forceFresh }))
        );
        settled.forEach(res => {
          if (res.status === 'fulfilled' && res.value) {
            const arr = res.value.transactions || [];
            if (arr.length) affAll.push(...arr);
          }
        });
      }

      setTransactionsAff(affAll);
      setTransactions([...(selfData.transactions || []), ...affAll]);

      setLoading(false);
    };
    run();
  }, [uid, isRegie, fetchPromoCodesForUid, getActiveAffiliateUidsForRegie]);

  // ===== KPIs (directs + indirects) — pour compatibilité interne
  useEffect(() => {
    // Directs (self)
    const currentSelf = getFilteredTransactionsFromList(transactionsSelf, 'current');
    setSumByCurrencyCurrent(sumListByCurrency(currentSelf));

    const nextSelf = getFilteredTransactionsFromList(transactionsSelf, 'next');
    setSumByCurrencyNext(sumListByCurrency(nextSelf));

    const totalSelf = getFilteredTransactionsFromList(transactionsSelf, 'total');
    setSumByCurrencyTotal(sumListByCurrency(totalSelf));

    // Indirects (affiliés)
    const currentAff = getFilteredTransactionsFromList(transactionsAff, 'current');
    setIndSumCurrent(sumListByCurrency(currentAff));

    const nextAff = getFilteredTransactionsFromList(transactionsAff, 'next');
    setIndSumNext(sumListByCurrency(nextAff));

    const totalAff = getFilteredTransactionsFromList(transactionsAff, 'total');
    setIndSumTotal(sumListByCurrency(totalAff));
  }, [transactionsSelf, transactionsAff, getFilteredTransactionsFromList]);

  // ======= NOUVEAU : Source “vue” selon sélecteur (pour les 3 KPI du haut) =======
  const ownerScopedRawList = useMemo(() => {
    if (!isRegie) return transactionsSelf;

    if (ownerFilter === 'all') return [...transactionsSelf, ...transactionsAff];
    if (ownerFilter === 'self') return [...transactionsSelf];
    if (ownerFilter === 'all_aff') return [...transactionsAff];
    // affilié précis
    return transactionsAff.filter(t => (t.__ownerUid || '') === ownerFilter);
  }, [isRegie, ownerFilter, transactionsSelf, transactionsAff]);

  // ======= NOUVEAU : calcul des 3 KPI d’en-tête en fonction du sélecteur =======
  useEffect(() => {
    const curList = getFilteredTransactionsFromList(ownerScopedRawList, 'current');
    const nxtList = getFilteredTransactionsFromList(ownerScopedRawList, 'next');
    setViewSumCurrent(sumListByCurrency(curList));
    setViewSumNext(sumListByCurrency(nxtList));

    // Compte clients uniques du mois courant (sur la source filtrée)
    const setUids = new Set();
    const now = new Date();
    const m = now.getMonth();
    const y = now.getFullYear();
    (ownerScopedRawList || []).forEach(trans => {
      const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
      timestamps.forEach(ts => {
        if (!ts?.datetime) return;
        const d = parseUsedTimestamp(ts.datetime);
        const uidE = extractEntrepriseUid(ts);
        if (d.getFullYear() === y && d.getMonth() === m) setUids.add(uidE);
      });
    });
    setViewClientsCount(setUids.size);
  }, [ownerScopedRawList, getFilteredTransactionsFromList]);

  // ===== Pseudos users — batch + cache session (entreprises et owners)
  const fetchUserPseudosBatch = async (uids) => {
    const unique = [...new Set((uids || []).filter(Boolean))];
    if (!unique.length) return {};
    const cache = readUsersCacheMap();
    const need = unique.filter(u => !(cache[u] && cache[u].pseudo));
    const CHUNK = 10;

    for (let i = 0; i < need.length; i += CHUNK) {
      const slice = need.slice(i, i + CHUNK);
      await Promise.all(slice.map(async (u) => {
        try {
          const ds = await getDoc(doc(db, 'users', u));
          if (ds.exists()) {
            const data = ds.data() || {};
            cache[u] = { pseudo: data.pseudo || data.displayName || u, photoURL: data.photoURL || null, ts: Date.now() };
          } else {
            cache[u] = { pseudo: u, photoURL: null, ts: Date.now() };
          }
        } catch {
          cache[u] = { pseudo: u, photoURL: null, ts: Date.now() };
        }
      }));
    }
    writeUsersCacheMap(cache);

    const out = {};
    unique.forEach(u => { out[u] = cache[u]?.pseudo || u; });
    return out;
  };

  // — Entreprise pseudos + Owner pseudos pour légendes/colonnes
  useEffect(() => {
    const run = async () => {
      const tableSource = isRegie ? transactions : transactionsSelf;
      const sectionList = getFilteredTransactionsFromList(tableSource, activeSection);

      const entUids = [...new Set(sectionList.map(t => extractEntrepriseUid(t.usedTimestamps)).filter(Boolean))];
      const ownerUids = [...new Set(sectionList.map(t => (t.__ownerUid || uid)).filter(Boolean))];
      const batch = await fetchUserPseudosBatch([...entUids, ...ownerUids]);
      setUserPseudos(prev => ({ ...prev, ...batch }));
    };
    if ((isRegie ? transactions : transactionsSelf).length > 0) run();
    // eslint-disable-next-line
  }, [transactions, transactionsSelf, activeSection, isRegie, getFilteredTransactionsFromList]);

  // ===== Logos =====
  const fetchCompanyLogo = async (entUid) => {
    if (!entUid) return null;

    const cachedUrl = readLogoCache(entUid);
    if (cachedUrl !== null) return cachedUrl;

    try {
      const ds = await getDoc(doc(db, 'users', entUid));
      if (ds.exists()) {
        const data = ds.data() || {};
        if (data.photoURL) {
          writeLogoCache(entUid, data.photoURL);
          return data.photoURL;
        }
      }
    } catch {}

    try {
      const logoFolderRef = ref(storage, `${entUid}/photo-profil`);
      const res = await listAll(logoFolderRef);
      if (res.items.length === 0) { writeLogoCache(entUid, null); return null; }
      const sortedItems = res.items.sort((a, b) => {
        const tsA = parseInt(a.name.split('_')[0], 10);
        const tsB = parseInt(b.name.split('_')[0], 10);
        return tsB - tsA;
      });
      const latestItem = sortedItems[0];
      const url = await getDownloadURL(latestItem);
      writeLogoCache(entUid, url);
      return url;
    } catch (error) {
      console.error("Erreur logo uid:", entUid, error);
      writeLogoCache(entUid, null);
      return null;
    }
  };

  useEffect(() => {
    const run = async () => {
      const tableSource = isRegie ? transactions : transactionsSelf;
      const sectionList = getFilteredTransactionsFromList(tableSource, activeSection);
      const entUids = [...new Set(sectionList.map(t => extractEntrepriseUid(t.usedTimestamps)).filter(Boolean))];
      const logos = {};
      await Promise.all(entUids.map(async (e) => {
        const url = await fetchCompanyLogo(e);
        logos[e] = url;
      }));
      setCompanyLogos(prev => ({ ...prev, ...logos }));
    };
    if ((isRegie ? transactions : transactionsSelf).length > 0) run();
    // eslint-disable-next-line
  }, [transactions, transactionsSelf, activeSection, isRegie, getFilteredTransactionsFromList]);

  // ===== Génération code promo (inchangé, mais on invalide le cache local) =====
  const handleGenerate = async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const usersCache = readUsersCacheMap();
      let userPseudo = usersCache[uid]?.pseudo || uid;
      if (userPseudo === uid) {
        try {
          const userDocRef  = doc(db, 'users', uid);
          const userDocSnap = await getDoc(userDocRef);
          userPseudo  = userDocSnap.exists() ? (userDocSnap.data().pseudo || uid) : uid;
          usersCache[uid] = { ...(usersCache[uid] || {}), pseudo: userPseudo, ts: Date.now() };
          writeUsersCacheMap(usersCache);
        } catch { /* noop */ }
      }

      const rootRef  = doc(db, 'code promo', uid);
      await setDoc(rootRef, { uid, pseudo: userPseudo }, { merge: true });

      invalidatePromosCache(uid);
      const refreshed = await fetchPromoCodesForUid(uid, { forceFresh: true });
      setPromo(refreshed.promo || null);
      setTransactionsSelf(refreshed.transactions || []);
      if (!isRegie) setTransactions(refreshed.transactions || []);
    } catch (error) {
      console.error("Erreur lors de la création du code promo :", error);
    } finally {
      setLoading(false);
    }
  };

  // Rend un total multi-devises joliment (EUR · USD · CAD · GBP · CHF · XOF)
  const renderMultiCurrencyTotal = (sumMap) => {
    const list = objectToCurrencyList(sumMap);
    if (!list.length) return formatAmount(0, 'EUR');
    return list.map(({currency, amount}) => formatAmount(amount, currency)).join(' · ');
  };

  // ———————— UI styles (scoped via unique classnames) ————————
  const EcobillUI = () => (
    <style>{`
  .aff-wrap { width: 100%; max-width: var(--eb-content-max); margin: 0 auto; }
  .aff-wrap .aff-h1 { margin: 0; text-align: left; }

  .aff-topbar { display:flex; align-items:flex-end; justify-content: space-between; gap: 12px 16px; margin: 0 0 24px; flex-wrap: wrap; }
  .aff-sub { color: var(--eb-muted); }

  .aff-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)); gap: 16px; margin: 0 0 32px; }
  .aff-card { background: var(--eb-surface); border:1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 20px 22px; box-shadow: var(--eb-shadow-sm); text-align:left; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
  .aff-card h3 { margin: 0; color: var(--eb-muted); font-size: 13.5px; font-weight: 600; text-align:left; letter-spacing: 0; }
  .aff-card .val { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); text-align:left; line-height: 1.2; word-break: break-word; }
  .aff-card .val--muted { font-size: 18px; color: var(--eb-muted); font-weight: 600; }
  .aff-card .val-unit { font-size: 14px; font-weight: 500; color: var(--eb-muted); margin-left: 6px; letter-spacing: 0; }

  /* Pills pour montants multi-devises dans les KPI */
  .aff-pill-wrap { display:flex; flex-wrap:wrap; gap:6px; justify-content:flex-start; align-items: center; color: var(--eb-muted); }
  .aff-pill { display:inline-block; padding:4px 10px; border-radius: var(--eb-radius-pill); background: var(--eb-brand-50); border:1px solid var(--eb-brand-100); font-weight:700; font-size: 15px; color: var(--eb-text); }

  .aff-section-bar .aff-section-title, .aff-section-bar .aff-tabs { margin: 0; }
  .aff-section-title { margin: 0 0 12px; font-size: 18px; font-weight: 650; color: var(--eb-text); text-align: left; }
  .aff-tabs { display: inline-flex; gap: 4px; align-items: center; flex-wrap: wrap; margin: 0 0 16px; padding: 4px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); max-width: 100%; }
  .aff-tab { cursor: pointer; min-height: 38px; padding: 0 16px; border-radius: var(--eb-radius-sm); background: transparent; color: var(--eb-text-2); border: 0; font-weight: 600; font-size: 14px; transition: background-color .15s ease, color .15s ease; }
  .aff-tab:hover { background: var(--eb-surface-2); color: var(--eb-text); }
  .aff-tab.is-active { background: var(--eb-brand); color: #fff; }

  /* —— Contrôles du sélecteur —— */
  .aff-controls { display:flex; justify-content:flex-end; align-items:center; gap:10px; }
  .aff-controls label { font-weight: 600; font-size: 14px; color: var(--eb-text-2); }
  .aff-select { min-height: 42px; border:1px solid var(--eb-border-strong); border-radius: var(--eb-radius-sm); padding: 0 12px; background: var(--eb-surface); color: var(--eb-text); font-weight: 500; font-size: 14.5px; max-width: 100%; }

  .aff-table-wrap { overflow: auto; border:1px solid var(--eb-border); border-radius: var(--eb-radius-lg); background: var(--eb-surface); box-shadow: var(--eb-shadow-sm); }
  table.aff-table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 860px; font-size: 14px; }
  .aff-table thead th { position: sticky; top: 0; background: var(--eb-surface-2); color: var(--eb-muted); text-align: left; font-size: 12.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 12px 16px; border-bottom: 1px solid var(--eb-border); white-space: nowrap; }
  .aff-table tbody td { padding: 14px 16px; border-bottom: 1px solid var(--eb-border); vertical-align: middle; color: var(--eb-text-2); }
  .aff-table tbody tr:last-child td { border-bottom: 0; }
  .aff-table tbody tr:hover { background: var(--eb-brand-50); }
  .aff-amt { text-align: right; font-weight: 700; color: var(--eb-text); white-space: nowrap; font-variant-numeric: tabular-nums; }

  .aff-company { display:flex; align-items:center; gap: 12px; }
  .aff-logo { width: 40px; height: 40px; border-radius: var(--eb-radius-sm); background: var(--eb-surface-2); border: 1px solid var(--eb-border); object-fit: cover; flex-shrink: 0; }
  .aff-company-name { font-weight: 600; color: var(--eb-text); }
  .aff-company-date { display:block; font-size: 13px; color: var(--eb-muted); margin-top:2px; }
  .aff-detail { font-size: 13px; color: var(--eb-muted); }

  /* Chip code + pseudo (adaptative) */
  .aff-chip { display:inline-flex; align-items:center; gap:4px; font-weight:600; padding:4px 10px; border-radius: var(--eb-radius-pill); border:1px solid var(--eb-border); background: var(--eb-surface-2); color: var(--eb-text-2); max-width:100%; align-self: flex-start; }
  .aff-chip.lg { font-size: 13px; }
  .aff-chip.md { font-size: 13px; }
  .aff-chip.sm { font-size: 12.5px; }
  .aff-chip.comm { background: var(--eb-brand-50); border-color: var(--eb-brand-100); color: var(--eb-brand); }
  .aff-chip.annual { background: var(--eb-success-bg); border-color: #c6f0da; color: var(--eb-success); }
  .aff-chip.resilie { background: var(--eb-danger-bg); border-color: #fbd0cb; color: var(--eb-danger); }
  .aff-chip.resilie .chip-owner { color: var(--eb-danger); }
  .chip-owner { font-weight: 700; color: var(--eb-brand); max-width: 180px; white-space: nowrap; overflow:hidden; text-overflow: ellipsis; }
  @media(min-width:1024px){ .chip-owner { max-width: 260px; } }
  .aff-alert { margin:12px 0; padding:12px 16px; border-radius: var(--eb-radius-sm); border:1px solid #fde3b0; background: var(--eb-warning-bg); color: var(--eb-warning); font-weight:600; }

  .aff-empty { text-align:center; color: var(--eb-muted); padding: 32px 16px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); font-size: 14.5px; }

  .aff-skeleton { background: linear-gradient(90deg, var(--eb-border) 25%, var(--eb-surface-2) 37%, var(--eb-border) 63%); border-radius: var(--eb-radius); background-size: 400% 100%; animation: shimmer 1.4s ease-in-out infinite; height: 56px; margin: 8px 0; }
  @keyframes shimmer { 0% { background-position: 100% 0 } 100% { background-position: 0 0 } }

  @media(min-width:641px) and (max-width:1100px){
    table.aff-table { min-width: 100%; }
    .aff-table thead th { padding: 12px; }
    .aff-table tbody td { padding: 12px; }
  }

  /* —— Mobile : lignes en cartes (≤640px) —— */
  @media(max-width: 640px){
    .aff-cards { gap: 10px; margin-bottom: 24px; }
    .aff-card { padding: 16px; }
    .aff-card .val { font-size: 24px; }
    table.aff-table { min-width: 0; border-spacing: 0; }
    .aff-table thead { display: none; }

    .aff-table tbody tr {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 14px 16px;
      border-bottom: 1px solid var(--eb-border);
    }
    .aff-table tbody tr:last-child { border-bottom: 0; }

    .aff-table tbody td {
      display: grid;
      grid-template-columns: 96px minmax(0, 1fr);
      align-items: center;
      padding: 4px 0;
      border: 0;
    }
    .aff-table tbody td::before {
      content: attr(data-col);
      font-size: 12.5px; color: var(--eb-muted); font-weight: 600;
    }

    .aff-table tbody td[data-col="Entreprise"] { order: 1; }
    .aff-table tbody td[data-col="Montant"]    { order: 2; }
    .aff-table tbody td[data-col="Code"]       { order: 3; }

    .aff-amt { text-align: left; }
    .aff-topbar { flex-direction: column; align-items: stretch; gap: 12px; }
    .aff-controls { justify-content: flex-start; width: 100%; }
    .aff-controls .aff-select { flex: 1; }
    .aff-tabs { display: flex; width: 100%; }
    .aff-tab { flex: 1; padding: 0 8px; }
  }

`}</style>

  );

  // ===== Rendu KPI montant : si 1 devise → texte gras simple. Si >1 → pastilles avec montants en gras.
  const KpiAmount = ({ sumMap }) => {
    if (shouldHideAmounts) {
      return (
        <div className="val val--muted">
          Indisponible
        </div>
      );
    }
    const list = objectToCurrencyList(sumMap);
    if (list.length <= 1) {
      return (<div className="val">{renderMultiCurrencyTotal(sumMap)}</div>);
    }
    return (
      <div className="aff-pill-wrap">
        {list.map(({ currency, amount }, i) => (
          <React.Fragment key={currency}>
            {i > 0 && <span style={{ fontWeight: 600 }}>+</span>}
            <span className="aff-pill">{formatAmount(amount, currency)}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ====== Options du sélecteur propriétaire ======
  const ownerOptions = useMemo(() => {
    if (!isRegie) {
      return [{ value: 'self', label: 'Vous' }];
    }
    const owners = new Set();
    (transactionsSelf || []).forEach(t => owners.add(t.__ownerUid || uid));
    (transactionsAff || []).forEach(t => owners.add(t.__ownerUid));

    const items = [];
    items.push({ value: 'all', label: 'Total' });
    items.push({ value: 'self', label: 'Régie' });
    items.push({ value: 'all_aff', label: 'Tous les affiliates' });
    Array.from(owners)
      .filter(o => o && o !== uid)
      .sort()
      .forEach(o => {
        const name = userPseudos[o] || o;
        items.push({ value: o, label: name });
      });
    return items;
  }, [isRegie, transactionsSelf, transactionsAff, userPseudos, uid]);

  // ====== Taille du chip selon longueur pseudo
  const getChipSize = (pseudo = '') => {
    const len = String(pseudo).length;
    if (len <= 10) return 'lg';
    if (len <= 18) return 'md';
    return 'sm';
  };

  // ===== Liste filtrée utilisée pour le tableau (régie = toutes transactions, sinon directes)
  const baseTableTransactions = getFilteredTransactionsFromList(
    isRegie ? transactions : transactionsSelf,
    activeSection
  );

  // Appliquer filtre "propriétaire" au tableau
  const tableTransactions = useMemo(() => {
    if (!isRegie) return baseTableTransactions;
    if (ownerFilter === 'all') return baseTableTransactions;
    if (ownerFilter === 'self') return baseTableTransactions.filter(t => (t.__ownerUid || uid) === uid);
    if (ownerFilter === 'all_aff') return baseTableTransactions.filter(t => (t.__ownerUid || '') !== uid);
    return baseTableTransactions.filter(t => (t.__ownerUid || '') === ownerFilter);
  }, [baseTableTransactions, ownerFilter, isRegie, uid]);


  return (
    <div>
      <AffiliatesNavbar />
      <div className="dashboard-container">
      <div className="aff-wrap">
        <EcobillUI />

        <div className="aff-topbar">
          <div>
            <h1 className="eb-page-title aff-h1">Transactions</h1>
            <p className="eb-page-subtitle">Suivi de vos commissions et des abonnements liés à vos codes.</p>
          </div>

          {/* ✅ Sélecteur déplacé sous le titre */}
          <div className="aff-controls">
            {isRegie ? (
              <>
                <label htmlFor="ownerFilter">Afficher :</label>
                <select
                  id="ownerFilter"
                  className="aff-select"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                >
                  {ownerOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </>
            ) : null}
          </div>
        </div>

        

        {/* ✅ 3 SEULS KPI (s’adaptent au sélecteur) — masqués pour un affilié rattaché à une régie */}
        {showKpiCards && (
          <div className="aff-cards" aria-label="Résumé (selon le filtre sélectionné)">
            <section className="aff-card" aria-labelledby="card-now">
              <h3 id="card-now">
                {new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
              </h3>
              <KpiAmount sumMap={viewSumCurrent} />
            </section>

            <section className="aff-card" aria-labelledby="card-next">
              <h3 id="card-next">
                {(() => {
                  const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1))
                    .toLocaleString('fr-FR', { month: 'long' })
                    .replace(/^\w/, c => c.toUpperCase());
                  return nextMonth;
                })()}
              </h3>
              <KpiAmount sumMap={viewSumNext} />
            </section>

            <section className="aff-card" aria-labelledby="card-clients">
              <h3 id="card-clients">
                {new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())} — Clients
              </h3>
              <div className="val">
                {viewClientsCount}
                <span className="val-unit">
                  Nouveaux clients
                </span>
              </div>
            </section>
          </div>
        )}

        {/* (Ancienne rangée Affiliates supprimée pour ne garder que 3 carrés) */}

        <div className="eb-toolbar aff-section-bar">
        <h2 className="aff-section-title">
          {isRegie ? 'Transactions' : 'Vos transactions'}
        </h2>

        {/* Tabs */}
        <div className="aff-tabs" role="tablist" aria-label="Sections de transactions">
          <button
            role="tab"
            aria-selected={activeSection === 'current'}
            className={`aff-tab ${activeSection === 'current' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('current')}
          >Mois en cours</button>
          <button
            role="tab"
            aria-selected={activeSection === 'next'}
            className={`aff-tab ${activeSection === 'next' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('next')}
          >Mois à venir</button>
          <button
            role="tab"
            aria-selected={activeSection === 'total'}
            className={`aff-tab ${activeSection === 'total' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('total')}
          >Total</button>
        </div>
        </div>

        {/* ⚠️ Sélecteur retiré d’ici (il est maintenant sous le titre) */}

        {/* Content */}
        <div>
          {loading ? (
            <>
              <div className="aff-skeleton" />
              <div className="aff-skeleton" />
              <div className="aff-skeleton" />
            </>
          ) : (
            promo ? (
              tableTransactions.length > 0 ? (
                <div className="aff-table-wrap" role="region" aria-label="Tableau des transactions">
                  <table className="aff-table">
                    <thead>
                      <tr>
                        <th>Entreprise</th>
                        <th>Code</th>
                        {showAmountColumn && <th style={{textAlign:'right'}}>Montant</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {tableTransactions.map((trans, index) => {
                        const ts = trans.usedTimestamps;
                        const rawDate = ts?.datetime || extractDateTime(ts);
                        const displayDate = formatRawDateForDisplay(rawDate);
                        const entrepriseUid = extractEntrepriseUid(ts);
                        const isCommission = !!trans.__isCommission;
                        const isAnnualProjection = !!trans.__isAnnualProjection;
                        const annualDetails = trans.__annualDetails || null;
                        const totalDetails = trans.__totalDetails || null;
                        const companyStatus = (trans.__companyStatus || ts?.__companyStatus || 'active').toLowerCase();
                        const isResiliated = companyStatus === 'inactive';
                        const amountDisplay = shouldHideAmounts ? '—' : formatAmount(trans.amount, trans.currency);
                        const amountTitle = shouldHideAmounts ? 'Montants gérés par votre régie' : amountDisplay;

                        // Pseudo du propriétaire du code (régie/affilié)
                        const ownerUid = trans.__ownerUid || uid;
                        const ownerPseudo = userPseudos[ownerUid] || ownerUid;
                        const chipSize = getChipSize(ownerPseudo);
                        const chipLabel = isResiliated
                          ? 'Résilié'
                          : (isCommission
                              ? 'Commission récurrente'
                              : (isAnnualProjection ? 'Abonnement annuel' : 'Abonnement mensuel'));
                        const chipModifiers = [
                          isCommission ? 'comm' : null,
                          isAnnualProjection ? 'annual' : null,
                          chipSize,
                          isResiliated ? 'resilie' : null,
                        ].filter(Boolean).join(' ');
                        const codeBadgeLabel = Number(trans.percentage || 0) > 0 ? 'Code remisé' : 'Code de base';
                        const showAnnualDetails = !shouldHideAmounts && isAnnualProjection && annualDetails && !isResiliated;
                        const showTotalDetails =
                          activeSection === 'total' &&
                          !shouldHideAmounts &&
                          !isResiliated &&
                          totalDetails &&
                          (totalDetails.baseMonthsApplied > 1 || totalDetails.recurringMonthsApplied > 0);

                        return (
                          <tr key={trans.__rowKey || `${trans.id}-${entrepriseUid}-${rawDate}-${index}`}>
                            <td data-col="Entreprise">
                              <div className="aff-company">
                                {companyLogos[entrepriseUid] ? (
                                  <img src={companyLogos[entrepriseUid]} alt="logo" className="aff-logo" />
                                ) : (
                                  <div className="aff-logo" />
                                )}
                                <div>
                                  <span className="aff-company-name">{userPseudos[entrepriseUid] || entrepriseUid}</span>
                                  <small className="aff-company-date">{displayDate}</small>
                                </div>
                              </div>
                            </td>
                            <td data-col="Code">
                              <div style={{ display:'flex', flexDirection:'column', gap:6, maxWidth:'100%' }}>
                                <span className={`aff-chip ${chipModifiers}`}>
                                  {chipLabel}
                                  <span className="chip-owner"> · {ownerPseudo}</span>
                                </span>
                                {!isCommission && (
                                  <div className="aff-detail">
                                    {codeBadgeLabel}
                                  </div>
                                )}
                                {showAnnualDetails && (
                                  <div className="aff-detail">
                                    Projection 12 mois : {formatAmount(annualDetails.firstTwoMonthsTotal, trans.currency)} (2 mois initiaux)
                                    {' + '}
                                    {annualDetails.recurringMonthsCount} × {formatAmount(annualDetails.recurringMonthlyAmount, trans.currency)}
                                  </div>
                                )}
                                {showTotalDetails && (
                                  <div className="aff-detail">
                                    Total cumulé : {totalDetails.baseMonthsApplied} × {formatAmount(totalDetails.baseAmount, trans.currency)} {' + '}
                                    {totalDetails.recurringMonthsApplied} × {formatAmount(totalDetails.recurringMonthlyAmount, trans.currency)}
                                  </div>
                                )}
                              </div>
                            </td>
                            {showAmountColumn && (
                              <td className="aff-amt" data-col="Montant" title={amountTitle}>{amountDisplay}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="aff-empty">Aucune transaction pour cette section.</div>
              )
            ) : (
              <button className="aff-cta eb-btn eb-btn--primary eb-btn--lg" onClick={handleGenerate}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Réclamer mon code promo
              </button>
            )
          )}
          
        </div>

      </div>
      </div>
    </div>
  );
};

export default AffiliatesTransactions;
